import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl, publicMediaSrc } from "../config/api";
import HomeSeo from "../components/HomeSeo";
import CategoryCard, { categoryHref, productHref, MEDIA_CARD_CLASS, MEDIA_CARD_GUTTER, MEDIA_CARD_GRID } from "../components/CategoryCard";
import { deliveryHref } from "../components/deliveryHref";
import viewAllCategoriesImg from "../assets/view-all-categories.jpg";
import viewAllProductsImg from "../assets/view-all-products.jpg";
import viewAllDeliveriesImg from "../assets/view-all-deliveries.jpg";
import factoryBg from "../assets/factory-bg.jpg";
import whyUsBg from "../assets/whyus-bg.jpg";
import factoryIcon from "../assets/factory-icon.png";
import yourSiteIcon from "../assets/yoursite-icon.png";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const CARD_GUTTER = MEDIA_CARD_GUTTER;
const CARD_GRID = `mt-8 ${MEDIA_CARD_GRID}`;
const CARD_CLASS = MEDIA_CARD_CLASS;

function pickPopularProducts(list) {
  const published = (Array.isArray(list) ? list : []).filter((item) => item?.isPublished !== false);
  const byCategory = new Map();
  for (const item of published) {
    const categoryId = item?.category?._id || item?.category;
    if (!categoryId) continue;
    const key = String(categoryId);
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(item);
  }
  const groups = [...byCategory.values()];
  for (let i = groups.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [groups[i], groups[j]] = [groups[j], groups[i]];
  }
  return groups.slice(0, 3).map((group) => group[Math.floor(Math.random() * group.length)]);
}

function formatDeliveryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function pickRecentDeliveries(list) {
  return (Array.isArray(list) ? list : [])
    .filter((item) => item?.isPublished !== false)
    .filter((item) => Array.isArray(item.images) && item.images[0]?.url)
    .sort((a, b) => new Date(b.deliveryDate || 0) - new Date(a.deliveryDate || 0))
    .slice(0, 3);
}

const whyUs = [
  {
    mark: "$",
    title: "Lower Price, Shorter Lead Time",
    blurb: "Our stockless model reduces warehouse and double-handling costs while keeping sourcing responsive.",
  },
  {
    mark: "#",
    title: "Loose or Bulk Quantities",
    blurb: "Order what your project needs — from loose pieces to full bulk quantities.",
  },
  {
    mark: "1",
    title: "Your One-Stop Trading Partner",
    blurb: "Source steel, hardware and industrial supplies through one reliable contact.",
  },
];

function Icon({ name, className = "h-6 w-6" }) {
  const common = {
    className,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6",
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
  };
  if (name === "truck") {
    return (
      <svg {...common}>
        <path d="M3 7h11v8H3z" />
        <path d="M14 10h4l3 3v2h-7" />
        <circle cx="7" cy="17" r="1.5" />
        <circle cx="18" cy="17" r="1.5" />
      </svg>
    );
  }
  if (name === "box") {
    return (
      <svg {...common}>
        <path d="M3 8l9-4 9 4-9 4-9-4z" />
        <path d="M3 8v8l9 4 9-4V8" />
        <path d="M12 12v8" />
      </svg>
    );
  }
  if (name === "gear") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v2M12 19v2M4.9 6.5l1.5 1.5M17.6 16l1.5 1.5M3 12h2M19 12h2M4.9 17.5l1.5-1.5M17.6 8l1.5-1.5" />
      </svg>
    );
  }
  return null;
}

function HomePage() {
  const [categories, setCategories] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [siteLogo, setSiteLogo] = useState(mediaUrl(HOME_MEDIA.logo));
  const [companyName, setCompanyName] = useState("");
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const published = (Array.isArray(list) ? list : [])
          .filter((item) => item?.isPublished !== false)
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
          .slice(0, 3);
        setCategories(published);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const current = Array.isArray(list) && list[0] ? list[0] : null;
        const logo = publicMediaSrc(current?.siteLogo);
        setContact(current);
        setSiteLogo(logo || mediaUrl(HOME_MEDIA.logo));
        setCompanyName(String(current?.companyName || "").trim());
      })
      .catch(() => {
        if (!cancelled) {
          setContact(null);
          setSiteLogo(mediaUrl(HOME_MEDIA.logo));
        }
      });
    fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setPopularProducts(pickPopularProducts(list));
      })
      .catch(() => {
        if (!cancelled) setPopularProducts([]);
      });
    fetch(`${getApiBaseUrl()}/deliveries`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setRecentDeliveries(pickRecentDeliveries(list));
      })
      .catch(() => {
        if (!cancelled) setRecentDeliveries([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const nodes = document.querySelectorAll("[data-reveal]");
    if (!nodes.length) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [categories.length, popularProducts.length, recentDeliveries.length]);

  return (
    <div>
      <HomeSeo contact={contact} />
      <section className="relative min-h-[420px] overflow-hidden bg-at-dark text-white md:min-h-[560px]">
        <img
          src={mediaUrl(HOME_MEDIA.hero)}
          alt="Steel pipes and industrial warehouse"
          className="hero-zoom pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="hero-overlay-clip pointer-events-none absolute inset-y-0 left-0 h-full w-full bg-black/[0.85] md:w-[62%]" />
        <div className="absolute bottom-5 right-16 z-10 hidden bg-black/90 px-5 py-3 text-left md:right-32 md:block lg:right-40">
          <p className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90">
            Materials
            <span className="inline-block w-12 border-t border-white/75" />
          </p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90">
            That build a stronger tomorrow
          </p>
        </div>

        <div className={`relative z-10 py-14 md:py-20 ${PAGE_GUTTER}`}>
          <div className="max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-at-gold">
              Steel / Hardware / Paint / Roofing
            </p>
            <h1 className="hero-in hero-in-1 mt-4 font-serif text-4xl leading-[1.08] sm:text-5xl md:text-[3.5rem]">
              Steel. Hardware.
              <span className="mt-1 block text-at-gold">Built for Industry.</span>
            </h1>
            <p className="hero-in hero-in-2 mt-5 max-w-md text-sm leading-relaxed text-white/70">
              Reliable industrial materials and hardware solutions for contractors, manufacturers and businesses across Malaysia.
            </p>
            <div className="hero-in hero-in-3 mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="hero-btn rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
              >
                Explore Products →
              </Link>
              <Link
                to="/contact"
                className="hero-btn rounded-sm border border-white/70 bg-black/40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black/60"
              >
                Contact Our Team
              </Link>
            </div>
            <ul className="mt-12 flex flex-wrap gap-8 text-xs text-white/80">
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.quality)} alt="" className="h-8 w-8 object-contain" />
                Quality Materials
              </li>
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.supply)} alt="" className="h-8 w-8 object-contain" />
                Reliable Supply
              </li>
              <li className="flex items-center gap-2">
                <img src={mediaUrl(HOME_MEDIA.trusted)} alt="" className="h-8 w-8 object-contain" />
                Trusted by Businesses
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={CARD_GUTTER}>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Our Products</p>
              <h2 className="mt-1 font-serif text-3xl text-at-dark">Product Categories</h2>
            </div>
            <Link to="/products" className="hidden text-sm text-neutral-500 hover:text-at-dark sm:inline">
              View All Products →
            </Link>
          </div>
          <div className={CARD_GRID}>
            {categories.map((item) => (
              <CategoryCard
                key={item._id || item.slug}
                to={categoryHref(item)}
                photo={publicMediaSrc(item.categoryPhoto)}
                alt={item.categoryPhotoAlt || item.categoryName || ""}
                title={item.categoryName}
                description={item.description}
                className={CARD_CLASS}
              />
            ))}
            <CategoryCard
              to="/category"
              photo={viewAllCategoriesImg}
              alt=""
              title="View All Categories"
              description="Explore our complete product range"
              className={CARD_CLASS}
              overlay={
                <span className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                  <span className="grid grid-cols-2 gap-1">
                    <span className="h-3.5 w-3.5 rounded-[3px] border-2 border-at-gold sm:h-4 sm:w-4" />
                    <span className="h-3.5 w-3.5 rounded-[3px] border-2 border-at-gold sm:h-4 sm:w-4" />
                    <span className="h-3.5 w-3.5 rounded-[3px] border-2 border-at-gold sm:h-4 sm:w-4" />
                    <span className="h-3.5 w-3.5 rounded-[3px] border-2 border-at-gold sm:h-4 sm:w-4" />
                  </span>
                </span>
              }
            />
          </div>
        </div>
      </section>

      <section className="about-section" data-reveal>
        <svg width="0" height="0" aria-hidden="true" focusable="false">
          <defs>
            <clipPath id="aboutCurve" clipPathUnits="objectBoundingBox">
              <path d="M 0 0 H 0.69 C 0.68 0.25, 0.67 0.55, 0.63 1 H 0 Z" />
            </clipPath>
          </defs>
        </svg>
        <img
          src={mediaUrl(HOME_MEDIA.about)}
          alt="AT Industrial work gloves"
          className="about-image"
        />
        <div className="about-content-background" />
        <div className="about-content">
          <div className="about-inner">
            <div className="about-copy">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">
                {companyName ? `About ${companyName}` : "About"}
              </p>
              <h2 className="mt-2 font-serif text-3xl uppercase leading-snug text-at-dark md:text-4xl">
                Materials that keep your{" "}
                <span className="text-at-gold">business moving.</span>
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                {companyName ? `${companyName} ` : ""}provides dependable steel, hardware and industrial products for commercial and industrial requirements. We focus on quality products, practical sourcing support and long-term partnerships with our customers.
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex rounded bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
              >
                Learn More About Us →
              </Link>
            </div>
            <ul className="about-features">
              <li className="flex gap-3">
                <Icon name="box" className="mt-0.5 h-6 w-6 shrink-0 text-at-gold" />
                <span>
                  <h3 className="block text-sm font-semibold text-at-dark">Broad Product Range</h3>
                  <span className="text-xs text-neutral-500">A wide selection of steel, hardware and industrial supplies.</span>
                </span>
              </li>
              <li className="flex gap-3">
                <Icon name="gear" className="mt-0.5 h-6 w-6 shrink-0 text-at-gold" />
                <span>
                  <h3 className="block text-sm font-semibold text-at-dark">Practical Sourcing Support</h3>
                  <span className="text-xs text-neutral-500">Help you source the right products for your requirements.</span>
                </span>
              </li>
              <li className="flex gap-3">
                <Icon name="truck" className="mt-0.5 h-6 w-6 shrink-0 text-at-gold" />
                <span>
                  <h3 className="block text-sm font-semibold text-at-dark">Delivery Coordination</h3>
                  <span className="text-xs text-neutral-500">Work with you to coordinate and facilitate deliveries.</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="factory-price-section relative overflow-hidden text-white" data-reveal>
        <img src={factoryBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className={`relative z-10 grid items-center gap-10 py-16 md:grid-cols-2 ${PAGE_GUTTER}`}>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              A one-stop trading partner for contractors
            </p>
            <h2 className="mt-3 font-serif text-3xl uppercase leading-snug md:text-4xl">
              Factory Price. Trader Speed.
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/75">
              We&apos;re a stockless trading house — no warehouse cost baked into your price, no double-handling markup. We source direct per order and get it to your site fast.
            </p>
            <p className="mt-5 flex items-start gap-2 text-xs text-white/70">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-at-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
                <circle cx="12" cy="10" r="2.2" />
              </svg>
              Based in Klang Valley, supplying across Malaysia.
            </p>
          </div>
          <div className="flex items-end justify-center gap-4 sm:gap-6">
            <div className="flex flex-col items-center text-center">
              <img src={factoryIcon} alt="" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">Supplier</span>
            </div>
            <span className="factory-arrow factory-arrow-1 mb-8 text-2xl text-at-gold">→</span>
            <div className="flex flex-col items-center text-center">
              <img src={siteLogo} alt="" className="h-14 w-14 object-contain invert sm:h-16 sm:w-16" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">AT Industrial</span>
            </div>
            <span className="factory-arrow factory-arrow-2 mb-8 text-2xl text-at-gold">→</span>
            <div className="flex flex-col items-center text-center">
              <img src={yourSiteIcon} alt="" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">Your Site</span>
            </div>
          </div>
        </div>
      </section>

      <section className="why-us-section relative overflow-hidden text-white">
        <img src={whyUsBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className={`relative z-10 py-16 ${PAGE_GUTTER}`}>
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
            The AT Industrial Difference
          </p>
          <h2 className="mt-2 text-center font-serif text-3xl uppercase md:text-4xl">Why Us?</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {whyUs.map((item, index) => (
              <article
                key={item.title}
                data-reveal
                data-reveal-delay={index === 0 ? undefined : String(index)}
                className="why-card border-t-2 border-at-gold bg-black/55 px-6 py-7"
              >
                <span className="flex h-10 w-10 items-center justify-center text-lg text-at-gold">
                  <svg viewBox="0 0 40 40" className="h-10 w-10">
                    <path d="M20 3l14 8v18L20 37 6 29V11z" fill="none" stroke="currentColor" strokeWidth="1.4" />
                    <text x="20" y="24" textAnchor="middle" fill="currentColor" fontSize="11" fontFamily="Manrope, sans-serif">{item.mark}</text>
                  </svg>
                </span>
                <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">{item.blurb}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={CARD_GUTTER}>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Featured Products</p>
              <h2 className="mt-1 font-serif text-3xl text-at-dark">Popular Products</h2>
            </div>
            <Link to="/products" className="hidden text-sm text-neutral-500 hover:text-at-dark sm:inline">
              View All Products →
            </Link>
          </div>
          <div className={CARD_GRID}>
            {popularProducts.map((item) => {
              const photo = publicMediaSrc(item.image);
              const categoryName = item.category?.categoryName || "";
              return (
                <Link
                  key={item._id || item.slug}
                  to={productHref(item)}
                  className={`media-card group relative flex flex-col justify-end overflow-hidden bg-at-dark p-4 text-white ${CARD_CLASS}`}
                >
                  {photo ? (
                    <img
                      src={photo}
                      alt={item.imageAlt || item.productName || ""}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : null}
                  <span className="absolute inset-0 bg-black/50" />
                  <span className="relative z-[2] flex items-end justify-between gap-2">
                    <span className="min-w-0">
                      <h3 className="block text-sm font-semibold leading-tight sm:text-base">{item.productName}</h3>
                      <span className="mt-0.5 min-h-[2.75em] line-clamp-2 text-[10px] leading-snug text-at-gold sm:text-xs">
                        {categoryName || "\u00a0"}
                      </span>
                    </span>
                    <span className="card-arrow shrink-0 text-lg text-at-gold">→</span>
                  </span>
                </Link>
              );
            })}
            <Link
              to="/products"
              className={`media-card group relative flex flex-col justify-end overflow-hidden bg-at-dark ${CARD_CLASS}`}
              aria-label="View all products"
            >
              <img
                src={viewAllProductsImg}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-14">
        <div className={CARD_GUTTER}>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Recent Deliveries</p>
              <h2 className="mt-1 font-serif text-3xl text-at-dark">Recent Deliveries</h2>
            </div>
            <Link to="/deliveries" className="hidden text-sm text-neutral-500 hover:text-at-dark sm:inline">
              View All Deliveries →
            </Link>
          </div>
          <div className={CARD_GRID}>
            {recentDeliveries.map((item) => {
              const firstImage = item.images[0];
              const photo = publicMediaSrc(firstImage.url);
              return (
                <Link
                  key={item._id || item.slug}
                  to={deliveryHref(item)}
                  className={`media-card group relative flex flex-col justify-end overflow-hidden bg-at-dark p-4 text-white ${CARD_CLASS}`}
                >
                  {photo ? (
                    <img
                      src={photo}
                      alt={firstImage.altText || item.title || ""}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : null}
                  <span className="absolute inset-0 bg-black/50" />
                  <span className="relative z-[2] flex items-end justify-between gap-2">
                    <span className="min-w-0">
                      <h3 className="block text-sm font-semibold leading-tight sm:text-base">{item.title}</h3>
                      <span className="mt-0.5 min-h-[2.75em] line-clamp-2 text-[10px] leading-snug text-at-gold sm:text-xs">
                        {formatDeliveryDate(item.deliveryDate) || "\u00a0"}
                      </span>
                    </span>
                    <span className="card-arrow shrink-0 text-lg text-at-gold">→</span>
                  </span>
                </Link>
              );
            })}
            <Link
              to="/deliveries"
              className={`media-card group relative flex flex-col justify-end overflow-hidden bg-at-dark ${CARD_CLASS}`}
              aria-label="View all deliveries"
            >
              <img
                src={viewAllDeliveriesImg}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
